import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { API_URL } from '../config';

/**
 * Help topics are markdown (in the repo, and as edited in the help panel). They
 * become HTML here, are sanitised with a help-only allowlist, and then get the
 * few things the panel needs:
 *
 * - [text](help:topic-id) opens another topic in the panel;
 * - [text](/learning) stays a real link (Ctrl-click works) that the panel turns
 *   into an in-app navigation;
 * - links elsewhere open in a new tab;
 * - images come only from /help/img/ (shipped in public/) or /api/help/images/
 *   (uploaded in the panel), load lazily, and are wrapped in a button that
 *   enlarges them.
 *
 * Anything else a topic might carry -- scripts, styles, event handlers, iframes,
 * images from other hosts -- is dropped.
 */
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'kbd', 'h1', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li', 'blockquote', 'hr', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td'];
const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'align'];
const IMAGE_PREFIXES = ['/help/img/', '/api/help/images/'];

export const HELP_LINK_PREFIX = 'help:';

/** The topic id a help: link points at, or null. */
export function helpLinkTarget(href: string | null | undefined): string | null {
  if (!href || !href.startsWith(HELP_LINK_PREFIX)) return null;
  const id = href.slice(HELP_LINK_PREFIX.length).trim();
  return /^[a-z0-9][a-z0-9-]{1,79}$/.test(id) ? id : null;
}

/** Every help: link in a markdown text, for the link checks in the tests. */
export function helpLinksIn(markdown: string): string[] {
  return [...markdown.matchAll(/\]\(\s*help:([^)\s]+)\s*\)/g)].map((m) => m[1]);
}

function toHtml(markdown: string): string {
  return marked.parse(markdown || '', { async: false, gfm: true, breaks: false }) as string;
}

function imageSrc(src: string): string | null {
  if (!IMAGE_PREFIXES.some((p) => src.startsWith(p)) || src.includes('..')) return null;
  return src.startsWith('/api/') ? `${API_URL}${src}` : src;
}

/** Topic markdown to safe HTML for the panel. `zoomLabel` names the enlarge button ("Enlarge image"). */
export function renderHelpMarkdown(markdown: string, zoomLabel = 'Enlarge image'): string {
  const clean = DOMPurify.sanitize(toHtml(markdown), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // help: is our own scheme; everything else follows DOMPurify's safe default list.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|help):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
  const template = document.createElement('template');
  template.innerHTML = clean;
  const root = template.content;

  root.querySelectorAll('a').forEach((a) => {
    const href = a.getAttribute('href') || '';
    const topic = helpLinkTarget(href);
    if (topic) {
      a.setAttribute('href', `#help-${topic}`);
      a.setAttribute('data-help-topic', topic);
    } else if (href.startsWith('/') && !href.startsWith('//')) {
      a.setAttribute('data-help-internal', '');
    } else if (/^(https?:|mailto:)/i.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    } else {
      a.removeAttribute('href');
    }
  });

  root.querySelectorAll('img').forEach((img) => {
    const src = imageSrc(img.getAttribute('src') || '');
    const alt = img.getAttribute('alt') || '';
    if (!src) {
      img.replaceWith(document.createTextNode(alt));
      return;
    }
    img.setAttribute('src', src);
    img.setAttribute('alt', alt);
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    img.removeAttribute('title');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'khelp-zoom';
    button.setAttribute('data-help-zoom', src);
    button.setAttribute('aria-label', alt ? `${zoomLabel}: ${alt}` : zoomLabel);
    img.replaceWith(button);
    button.appendChild(img);
  });

  // Wide tables scroll inside the panel instead of pushing it sideways.
  root.querySelectorAll('table').forEach((table) => {
    const wrap = document.createElement('div');
    wrap.className = 'khelp-table';
    table.replaceWith(wrap);
    wrap.appendChild(table);
  });

  const out = document.createElement('div');
  out.appendChild(root.cloneNode(true));
  return out.innerHTML;
}

/** The words of a topic, without markup, for search and snippets. */
export function helpPlainText(markdown: string): string {
  const html = DOMPurify.sanitize(toHtml(markdown), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}
